import { type FC, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import PostLossPaywall from "@/pages/postloss/components/PostLossPaywall";
import { KeyDocumentsUploadCard } from "@/pages/postloss/documents/components/KeyDocumentsUploadCard";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useAuthorityAnswers } from "@/shared/hooks/useAuthorityAnswers";
import { useAuthorityContext } from "@/shared/hooks/useAuthorityContext";
import { useCanonicalAddress } from "@/shared/hooks/useCanonicalAddress";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useUserEstateRole } from "@/shared/hooks/useUserEstateRole";
import { contactFullName } from "@/shared/utils/contactUtils";
import { getEstateSize } from "@/shared/utils/estateSize";

import { AboutSection } from "./components/AboutSection";
import { EstateLocationSection } from "./components/EstateLocationSection";
import { FamilySection } from "./components/FamilySection";
import { CourtAuthoritySection, WillTrustSection } from "./components/ReadOnlySections";
import { SummaryHeader } from "./components/SummaryHeader";
import { SummaryTiles, type Fiduciary } from "./components/SummaryTiles";
import { estateSummaryRecordsKey, useEstateSummaryRecords } from "./hooks/useEstateSummaryRecords";
import { findContact, formatShortDate, getStateName } from "./summaryFormat";

type EditableSection = "about" | "location" | "family";

const FIDUCIARY_TITLES: Record<string, string> = {
  executor: "Executor",
  administrator: "Administrator",
  trustee: "Trustee",
};

export const SummaryPage: FC = () => {
  const queryClient = useQueryClient();
  const { person, user, refetch } = useAppContext();
  const estateId = person?.estate_id;
  const [editing, setEditing] = useState<EditableSection | null>(null);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);

  const { data: records } = useEstateSummaryRecords(estateId);
  const { data: domicile } = useCanonicalAddress(estateId, "domicile", { includePartial: true });
  const { assets } = useEstateItems(estateId);
  const { contacts } = useUserContacts();
  const { path: authorityPath } = useAuthorityContext(estateId);
  const { savedAnswers: authorityAnswers } = useAuthorityAnswers(estateId);
  const { hasLegalAuthority, legalRole } = useUserEstateRole();

  if (!person || !estateId) {
    return <MainLayout header="Summary" actions={<PageActions />} />;
  }

  const primaryCase = records?.primaryCase ?? null;
  const estateSize = getEstateSize(assets);
  const firstName = person.first_name || person.full_name?.split(" ")[0] || "";

  const caseFiduciary = findContact(contacts, primaryCase?.fiduciary_contact_id);
  const userName = [user?.first_name, user?.last_name].filter(Boolean).join(" ");
  const fiduciary: Fiduciary | null = caseFiduciary
    ? {
        name: contactFullName(caseFiduciary),
        title: FIDUCIARY_TITLES[primaryCase?.fiduciary_title ?? ""] ?? "Executor",
        appointedOn: formatShortDate(primaryCase?.issuance_date),
      }
    : authorityAnswers?.appointment === "someone-else" && authorityAnswers.appointment_name
      ? { name: authorityAnswers.appointment_name, title: "Executor", appointedOn: "" }
      : hasLegalAuthority && userName
        ? { name: userName, title: FIDUCIARY_TITLES[legalRole] ?? "Executor", appointedOn: "" }
        : null;

  const sectionEdit = (section: EditableSection) => ({
    isEditing: editing === section,
    canEdit: editing === null,
    onEdit: () => setEditing(section),
    onClose: () => setEditing(null),
  });

  const refreshFamily = () => {
    refetch();
    void queryClient.invalidateQueries({ queryKey: estateSummaryRecordsKey(estateId) });
  };

  return (
    <MainLayout header="Summary" actions={<PageActions />}>
      <div className="flex w-full flex-col gap-6 py-6">
        <SummaryHeader estate={person} />
        <SummaryTiles
          firstName={firstName}
          caseType={primaryCase?.case_type ?? null}
          authorityPath={authorityPath}
          fiduciary={fiduciary}
          estateSize={estateSize}
          state={getStateName(domicile?.state)}
          onAddAddress={() => setEditing("location")}
        />
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="flex min-w-0 flex-1 flex-col gap-6">
            <AboutSection estate={person} {...sectionEdit("about")} />
            <EstateLocationSection
              estateId={estateId}
              domicile={domicile}
              caseCourtName={primaryCase?.courts?.name ?? null}
              propertyStates={records?.propertyStates ?? []}
              estateSize={estateSize}
              {...sectionEdit("location")}
            />
            <WillTrustSection
              will={records?.will ?? null}
              hasTrust={person.has_trust}
              trust={records?.trusts[0]}
              contacts={contacts}
            />
            <CourtAuthoritySection estateCase={primaryCase} contacts={contacts} />
            <FamilySection
              estate={person}
              marriageHistory={records?.marriageHistory ?? []}
              onSaved={refreshFamily}
              {...sectionEdit("family")}
            />
          </div>
          <KeyDocumentsUploadCard
            onPaywall={() => setIsPaywallOpen(true)}
            className="w-full shrink-0 lg:w-[300px]"
          />
        </div>
      </div>
      <PostLossPaywall
        isOpen={isPaywallOpen}
        onOpenChange={setIsPaywallOpen}
        promptLocation="document_limit"
        returnPath={POST_LOSS_ROUTES.SUMMARY}
      />
    </MainLayout>
  );
};
